import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Chip, DataTable, Meter } from "./DataTable";
import { StatTile } from "./Evidence";
import { DistrictProfile, StateBriefCard } from "./Briefs";
import { BoardNote } from "./BoardNote";
import { inr, num, pct, score } from "../lib/format";

const plural = (n, word) => `${num(n)} ${word}${Number(n) === 1 ? "" : "s"}`;
const Dot = () => <span className="text-stone-500">·</span>;

/** Shows the arithmetic behind a derived figure, so no number is unexplained. */
const cpbTooltip = (r, scope) =>
  r.cost_per_beneficiary_internal
    ? `${inr(r.cost_per_beneficiary_internal)} = ${inr(r.funds_utilised ?? r.utilised)} utilised ` +
      `÷ ${num(r.beneficiaries_reached)} beneficiaries declared as reached\n` +
      `Source: mGrant${scope ? ` · ${scope}` : ""}\n` +
      `Beneficiaries are as declared on the grant, counted once per grant. Withheld below ` +
      `10 declared. mGrant-internal comparison only — never compared to an external figure.`
    : r.cost_per_beneficiary_withheld || "no beneficiary count declared";

const utilTooltip = (r) =>
  `${r.fund_utilisation_rate ?? r.utilisation_rate_pct}% = ${inr(r.funds_utilised ?? r.utilised)} ` +
  `utilised ÷ ${inr(r.funds_received ?? r.received)} received\n` +
  `Utilisation of funds received, not of the planned budget. Source: mGrant.`;

/** Registry due-diligence chips: the partner's own disclosures, never blended with mGrant. */
const registryTooltip = (r) => {
  if (!r.registry_matched) return r.registry_match_note;
  const lines = [
    `${r.ngo_name_in_registry}${r.pan ? ` · PAN ${r.pan}` : ""}`,
    `Registrations on file: ${(r.registration_types || []).join(", ") || "none"}`,
    r.csr_eligible
      ? "CSR-eligible: 12A, 80G and CSR Form-1 all present"
      : `Not CSR-eligible in this snapshot — missing ${(r.csr_eligibility_missing || []).join(", ")}`,
    r.fcra_registered ? "FCRA registered" : "No FCRA registration in this snapshot",
  ];
  if (r.programme_expense_ratio_pct != null) {
    lines.push(
      `Programme expense ratio ${r.programme_expense_ratio_pct}% = ` +
        `${inr(r.own_total_expenses_inr)} total expenditure, of which programmes, in the ` +
        `partner's own ${r.own_reporting_year} accounts`
    );
  }
  if (r.funder_dependency_pct != null) {
    lines.push(
      `Our weight in their budget ${r.funder_dependency_pct}% = ` +
        `${inr(r.biggest_bajaj_fy_committed_inr)} committed in ${r.biggest_bajaj_fy} (mGrant) ` +
        `÷ ${inr(r.own_total_expenses_inr)} their total expenditure in ${r.own_reporting_year}`
    );
  }
  if (r.absorption_capacity_flag) {
    lines.push(
      `Absorption signal: our ${r.biggest_bajaj_fy} commitment of ` +
        `${inr(r.biggest_bajaj_fy_committed_inr)} exceeds their largest reported year of spend ` +
        `(${inr(r.own_peak_annual_expenses_inr)})`
    );
  }
  lines.push(`Matched on ${r.registry_match_method?.split("_").join(" ")}.`);
  lines.push(`Source: ${r.registry_source} — the partner's own disclosures, unaudited by us.`);
  return lines.join("\n");
};

/** First-party mGrant due diligence. Primary; give.do is only a cross-check. */
const ddTooltip = (r, caveats = {}) => {
  const lines = [`${r.dd_status_label}${r.dd_id ? ` · ${r.dd_id}` : ""}`];
  const missing = r.dd_missing_caveat || caveats.dd_missing;
  const assessment = r.dd_assessment_caveat || caveats.dd_assessment;
  if (r.dd_gap_flag && missing) lines.push(missing);
  if (r.mgrant_fcra_status) lines.push(`FCRA status in mGrant: ${r.mgrant_fcra_status}`);
  if (r.mgrant_ngo_status) lines.push(`Partner status: ${r.mgrant_ngo_status}`);
  if (r.dd_regulatory_watchlist) {
    lines.push(
      `Regulatory watchlist: ${r.dd_regulatory_watchlist}` +
        (r.dd_regulatory_watchlist_verbatim
          ? `\n   analyst wrote: "${r.dd_regulatory_watchlist_verbatim}"`
          : "")
    );
  }
  if (r.dd_blacklist_verification) lines.push(`Blacklist check: ${r.dd_blacklist_verification}`);
  if (r.dd_political_affiliation) lines.push(`Political affiliation: ${r.dd_political_affiliation}`);
  if (r.dd_guidestar_listed) lines.push(`GuideStar India listed: ${r.dd_guidestar_listed}`);
  if (r.dd_has_areas_of_concern) lines.push(`Areas of concern recorded: "${r.dd_areas_of_concern_verbatim}"`);
  if (r.dd_site_visit_verbatim) lines.push(`Site visit: ${r.dd_site_visit_verbatim}`);
  if (r.dd_source) lines.push(`Source: ${r.dd_source}`);
  if (assessment) lines.push(assessment);
  return lines.join("\n");
};

/** NITI Aayog aspirational-district standing. Three-valued, never a bare yes/no. */
const AdpChip = ({ r }) => {
  if (!r.adp_status || r.adp_status === "no_district") return null;
  // Only the exceptions are badged. A label on every ordinary district is noise.
  if (r.adp_status === "not_aspirational") return null;
  const map = {
    aspirational: ["aspirational district", "review",
      `${r.adp_district_name || r.cell} is on NITI Aayog's list of 112 aspirational districts.\n${r.adp_source}`],
    not_in_published_list: ["not in published list", "muted",
      r.adp_caveat || "Not itemised in the published list."],
  };
  const hit = map[r.adp_status];
  if (!hit) return null;
  const [label, tone, title] = hit;
  return <Chip label={label} tone={tone} title={title} />;
};

/** BFSI peer set: always shown as a range, with the sub-sector split behind it. */
const BFSI_SUBSECTORS = {
  bank: "Banks",
  insurance: "Insurance",
  nbfc_finance: "NBFCs & lending",
  capital_markets: "Capital markets & asset management",
  fintech_payments: "Payments & fintech",
};

const bfsiTooltip = (r) => {
  if (r.bfsi_spent_inr === null) return r.bfsi_withheld_reason;
  const lines = [
    `BFSI peer spend in ${r.fy}: ${inr(r.bfsi_spent_inr)} across ${num(r.bfsi_company_count)} companies`,
    `Upper bound with the wider tagging: ${inr(r.bfsi_spent_inr_upper)} across ${num(r.bfsi_company_count_upper)} companies`,
    `Bajaj Finserv filed ${inr(r.bajaj_mca_filed_inr)} — ${pct(r.bajaj_share_of_bfsi_pct, 1)} of the core set, ${pct(r.bajaj_share_of_bfsi_pct_lower, 1)} of the wider set`,
    r.bfsi_share_of_all_peers_pct != null
      ? `BFSI is ${pct(r.bfsi_share_of_all_peers_pct, 1)} of all peer CSR spend in this cell`
      : null,
    "",
    "Sub-sector split (core + medium):",
    ...Object.entries(r.bfsi_subsector_spend_inr || {}).map(
      ([k, v]) => `   ${BFSI_SUBSECTORS[k] || k}: ${inr(v)}`
    ),
    "",
    "Tagged from the filed company name — the MCA file has no industry code. Bajaj Finserv's own entities are inside this set.",
  ];
  return lines.filter((x) => x !== null).join("\n");
};

const DD_TONE = { approved: "consistent", pending: "review", none: "erratic" };const DD_LABEL = { approved: "DD approved", pending: "DD pending", none: "no DD record" };

const DiligenceCell = ({ r, caveats }) => (
  <div className="inline-flex flex-col items-end gap-1" title={ddTooltip(r, caveats)}>
    <Chip label={DD_LABEL[r.dd_status]} tone={DD_TONE[r.dd_status]} />
    <span className="inline-flex gap-1">
      {r.dd_regulatory_watchlist && r.dd_regulatory_watchlist !== "unclassified" && (
        <Chip label={`risk ${r.dd_regulatory_watchlist.toLowerCase()}`}
              tone={r.dd_regulatory_watchlist === "Low" ? "consistent" : "review"} />
      )}
      {r.dd_has_areas_of_concern && <Chip label="concern noted" tone="review" />}
      {r.dd_blacklist_verification === "something recorded — read the note" && (
        <Chip label="adverse media" tone="erratic" title={r.dd_blacklist_verification_verbatim} />
      )}
      {r.mgrant_fcra_status === "FCRA" && <Chip label="FCRA" tone="muted" />}
    </span>
  </div>
);

const RegistryCell = ({ r }) => {
  if (!r.registry_matched) {
    return <Chip label="not in registry" tone="muted" title={registryTooltip(r)} />;
  }
  return (
    <div className="inline-flex flex-col items-end gap-1" title={registryTooltip(r)}>
      <span className="inline-flex items-center gap-1.5">
        {r.programme_expense_ratio_pct != null ? (
          <span className="num text-stone-800">
            {pct(r.programme_expense_ratio_pct)}
          </span>
        ) : (
          <span className="text-stone-500">no accounts</span>
        )}
        <Chip label={r.csr_eligible ? "CSR ok" : "CSR gap"}
              tone={r.csr_eligible ? "consistent" : "review"} />
      </span>
      {(r.funder_dependency_flag || r.absorption_capacity_flag) && (
        <span className="inline-flex gap-1">
          {r.funder_dependency_flag && <Chip label={`we are ${r.funder_dependency_pct}%`} tone="review" />}
          {r.absorption_capacity_flag && <Chip label="capacity" tone="erratic" />}
        </span>
      )}
    </div>
  );
};

/** Traces a state-level committed figure back to the grants and years behind it. */
export const grantTooltip = (r) => {
  const theme = r.theme_label ? `${r.theme_label} theme only` : "this theme only";
  const lines = (r.bajaj_grant_detail || []).map(
    (g) =>
      `${g.fy}  ${g.grant_id}  ${g.partner}\n` +
      `   ${g.grant_name}\n` +
      `   ${inr(g.committed_in_state)} of ${inr(g.committed_total)}` +
      (g.states_split_across > 1
        ? ` — even split across ${g.states_split_across} states${g.states_split_across >= 6 ? " (pan-India, apportioned)" : ""}`
        : " — single-state grant")
  );
  const head = r.bajaj_apportioned_only
    ? `APPORTIONED, NOT TARGETED · ${theme}\nNo grant is aimed at ${r.state_name}. This figure is ` +
      `only an even split of pan-India grants covering up to ${r.bajaj_max_states_split} states, ` +
      `so it is not a commitment to ${r.state_name}.\n\n`
    : `${r.bajaj_grants} grant(s) in ${r.state_name} · ${theme}\n` +
      `${r.bajaj_fy_range?.join(", ")}\n` +
      `${inr(r.bajaj_targeted_inr)} aimed at ${r.state_name} across ${r.bajaj_targeted_grants} grant(s)` +
      (r.bajaj_apportioned_inr
        ? `\n${inr(r.bajaj_apportioned_inr)} is this state's even share of ${r.bajaj_apportioned_grants} pan-India grant(s)`
        : "") +
      "\n\n";
  return head + lines.join("\n");
};

/** Direct vs apportioned split shown under a state's committed figure. */
const SplitNote = ({ r }) => {
  if (!r.bajaj_apportioned_inr) return null;
  if (r.bajaj_apportioned_only) {
    return (
      <div className="text-[12px] text-amber-700 mt-0.5 font-sans">
        all apportioned · no grant aimed here
      </div>
    );
  }
  return (
    <div className="text-[12px] text-stone-600 mt-0.5 font-sans">
      {inr(r.bajaj_targeted_inr)} aimed here + {inr(r.bajaj_apportioned_inr)} pan-India share
    </div>
  );
};

/**
 * Need against funding, as a gap rather than a bar. Each row shows how much need the state
 * carries, where our funding share sits on the same 0-100 scale, and the distance between them —
 * which is the number the index actually reports.
 */
const NeedGapChart = ({ rows, themeLabel, onRowClick }) => (
  <div className="rounded-xl border border-stone-200 bg-white divide-y divide-stone-100"
       data-testid="need-gap-chart">
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <div className="text-[12px] font-semibold text-stone-600">
        Need above funding · widest gaps first
      </div>
      <div className="flex items-center gap-4 text-[12px] text-stone-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-6 h-1.5 rounded-full bg-[#A01236]/25" /> need
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#A01236] ring-2 ring-white shadow-sm" />
          share of {themeLabel} funding
        </span>
      </div>
    </div>
    {rows.map((r) => (
      <button
        key={r.state_name}
        onClick={() => onRowClick?.(r)}
        data-testid={`need-gap-row-${r.state_name}`}
        className="w-full flex items-center gap-4 px-4 py-2.5 text-left hover:bg-[#FDF2F4]/60 transition-colors"
      >
        <span className="w-32 shrink-0 text-[13px] font-medium text-stone-800 truncate">
          {r.state_name}
        </span>
        <span className="flex-1 relative h-5">
          <span className="absolute inset-y-[7px] left-0 right-0 bg-stone-100 rounded-full" />
          <span className="absolute inset-y-[7px] left-0 bg-[#A01236]/25 rounded-full"
                style={{ width: `${Math.max(1, r.need_score)}%` }} />
          <span
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#A01236] ring-2 ring-white shadow-sm"
            style={{ left: `${Math.min(100, Math.max(0, r.funding_score))}%` }}
            title={`Funding score ${score(r.funding_score)} · ${pct(r.bajaj_share_of_theme_portfolio_pct, 2)} of the committed ${themeLabel} portfolio`}
          />
        </span>
        <span className="w-24 shrink-0 text-right">
          <span className={`num text-[14px] font-semibold ${
            r.need_vs_funding_index > 40 ? "text-[#A01236]"
              : r.need_vs_funding_index > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {r.need_vs_funding_index > 0 ? "+" : ""}{score(r.need_vs_funding_index)}
          </span>
        </span>
        <span className="w-20 shrink-0 text-right num text-[12px] text-stone-600">
          {r.bajaj_committed_inr ? inr(r.bajaj_committed_inr) : "—"}
        </span>
      </button>
    ))}
  </div>
);

const MiniBar = ({ data, xKey, yKey, label, colorFn }) => (
  <div className="h-44 -ml-2" data-testid="evidence-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
        <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "#57534E" }} interval={0}
               height={28} tickLine={false} axisLine={{ stroke: "#E7E5E4" }} />
        <YAxis tick={{ fontSize: 10, fill: "#78716C" }} width={52} tickLine={false} axisLine={false}
               tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} />
        <Tooltip
          contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }}
          formatter={(v) => [label?.includes("₹") ? inr(v) : num(v), label]}
        />
        <Bar dataKey={yKey} radius={[3, 3, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={colorFn ? colorFn(d) : "#A01236"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const ResultNumbers = ({ result, onPartnerClick, onRowClick, onDistrictClick }) => {
  if (!result) return null;

  if (result.kind === "board_note") return <BoardNote note={result} />;

  if (result.kind === "state_brief_multi") {
    return (
      <div className="space-y-4" data-testid="state-brief-multi">
        {result.briefs.length > 1 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5">
            <StatTile label="Committed across these states" tone="brand"
                      value={inr(result.combined_committed_inr)}
                      sub={`${num(result.combined_grants)} grants`} />
            <StatTile label="States covered" tone="sky" value={num(result.briefs.length)}
                      sub={result.states_without_grants?.length
                        ? `${result.states_without_grants.join(", ")} has no grants`
                        : "all named states funded"} />
            <StatTile label="Aspirational districts funded" tone="brand"
                      value={num(result.combined_aspirational_districts?.length || 0)}
                      sub="across these states" />
          </div>
        )}
        {result.briefs.map((b) => (
          <StateBriefCard key={b.state} brief={b} compact />
        ))}
      </div>
    );
  }

  if (result.kind === "state_brief") return <StateBriefCard brief={result} />;
  if (result.kind === "district_profile") return <DistrictProfile profile={result} />;

  if (result.kind === "aspirational_districts") {
    const rows = result.rows || [];
    return (
      <div className="space-y-4" data-testid="adp-result">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <Stat label={`On the list in ${result.scope_label}`}
                value={`${num(result.districts_funded)} of ${num(result.districts_in_list)} funded`} />
          <Stat label="Committed into them" value={inr(result.committed_in_aspirational_inr)} />
          <Stat label="Share of whole portfolio"
                value={pct(result.share_of_portfolio_pct)}
                title={`Of ${inr(result.portfolio_committed_inr)} committed across the portfolio. `
                  + `${pct(result.share_of_district_mapped_portfolio_pct)} of the `
                  + `${inr(result.district_mapped_committed_inr)} that is district-mapped.`} />
          <Stat label={result.state ? `Share of ${result.state} spend` : "Districts funded nationally"}
                value={result.state
                  ? pct(result.share_of_state_portfolio_pct)
                  : `${num(result.national_districts_funded)} of ${num(result.national_districts_in_list)}`} />
        </div>
        <DataTable
          testId="adp-table"
          maxHeight="max-h-[30rem]"
          minWidth="min-w-[34rem]"
          rows={rows}
          defaultSort={{ key: "committed_inr", order: "desc" }}
          columns={[
            { key: "label", label: "Aspirational district", sortValue: (r) => r.label,
              render: (r) => (
                <div>
                  <span className="font-medium text-stone-900">{r.label}</span>
                  {r.partners.length > 0 && (
                    <div className="text-[12px] text-stone-600 truncate max-w-[22rem]">
                      {r.partners.slice(0, 3).join(", ")}
                      {r.partners.length > 3 && ` +${r.partners.length - 3} more`}
                    </div>
                  )}
                </div>
              ) },
            { key: "committed_inr", label: "Committed", align: "right",
              headerTitle: "Committed value apportioned evenly where a grant names several "
                + "districts. Intent across the grant period, not annual spend.",
              className: "whitespace-nowrap", sortValue: (r) => r.committed_inr,
              render: (r) => r.funded
                ? inr(r.committed_inr)
                : <Chip label="we fund nothing here" tone="review" /> },
            { key: "grants", label: "Grants", align: "right", sortValue: (r) => r.grants,
              render: (r) => r.grants ? num(r.grants) : <span className="text-stone-500">—</span> },
            { key: "utilisation_rate_pct", label: "Utilisation", align: "right",
              headerTitle: "Utilised over received on the grants whose period has ended.",
              className: "whitespace-nowrap", sortValue: (r) => r.utilisation_rate_pct,
              render: (r) => r.utilisation_rate_pct == null
                ? <span className="text-stone-500">—</span>
                : <Meter value={r.utilisation_rate_pct} tone="good" /> },
          ]}
        />
        {result.not_funded?.length > 0 && (
          <div className="text-[13px] text-stone-700" data-testid="adp-not-funded">
            <span className="font-semibold">On the list, but we fund nothing there:</span>{" "}
            {result.not_funded.join(" · ")}
          </div>
        )}
      </div>
    );
  }

  if (result.kind === "partner_performance") {
    const rows = (result.rows || []).slice(0, 60);
    const maxCommitted = Math.max(...rows.map((r) => r.committed || 0), 1);
    return (
      <div className="space-y-4">
        <DataTable
          testId="partner-table"
          onRowClick={onPartnerClick}
          rows={rows}
          defaultSort={{ key: "committed", order: "desc" }}
          columns={[
            { key: "partner", label: "Partner", sortValue: (r) => r.partner, render: (r) => (
              <div className="max-w-sm">
                <div className="font-medium text-stone-900 leading-snug">{r.partner}</div>
                <div className="text-[12px] text-stone-600 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span>{plural(r.grants, "grant")}</span>
                  <Dot />
                  <span>{plural(r.fys.length, "FY")}</span>
                  <Dot />
                  <span>{plural(r.district_count, "district")}</span>
                  <Dot />
                  <span className="text-stone-600">{r.themes.join(" · ")}</span>
                </div>
              </div>
            )},
            { key: "committed", label: "Committed", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.committed, render: (r) => (
                <span className="inline-flex items-center gap-2.5 justify-end">
                  <span className="text-stone-900">{inr(r.committed)}</span>
                  <span className="w-12 h-1.5 bg-stone-100 rounded-full overflow-hidden shrink-0">
                    <span className="block h-full rounded-full bg-[#A01236]/70"
                          style={{ width: `${(100 * (r.committed || 0)) / maxCommitted}%` }} />
                  </span>
                </span>
              )},
            { key: "closed_utilisation_rate", label: "Utilisation", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.closed_utilisation_rate ?? 101,
              render: (r) => r.closed_utilisation_rate === null || r.closed_utilisation_rate === undefined
                ? <Chip label="in flight" tone="muted" title={`All ${r.grants} grants are still inside their period or in an open financial year; utilisation is not judged yet.`} />
                : <span title={`${r.closed_utilisation_rate}% = utilised ÷ ${inr(r.closed_funds_received)} received on ${r.closed_grants} ended grant${r.closed_grants === 1 ? "" : "s"}${r.in_flight_grants ? `; ${r.in_flight_grants} still in flight` : ""}. Across every grant: ${r.fund_utilisation_rate}%.`}>
                    <Meter value={r.closed_utilisation_rate} /></span> },
            { key: "closed_funds_unspent", label: "Unutilised", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.closed_funds_unspent,
              render: (r) => <span title={`Funds received minus funds utilised on ${r.closed_grants} ended grant${r.closed_grants === 1 ? "" : "s"}. Across every grant, including in flight: ${inr(r.funds_unspent)} unspent.`}>{inr(r.closed_funds_unspent)}</span> },
            { key: "cost_per_beneficiary_internal", label: "₹ / beneficiary*", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.cost_per_beneficiary_internal,
              render: (r) => r.cost_per_beneficiary_internal
                ? <span title={cpbTooltip(r, r.fys?.join(", "))}>{inr(r.cost_per_beneficiary_internal)}</span>
                : <Chip label="withheld" tone="muted" title={r.cost_per_beneficiary_withheld || "no beneficiary count declared"} /> },
            { key: "dd_status", label: "Diligence · mGrant", align: "right",
              className: "whitespace-nowrap",
              sortValue: (r) => ({ none: 0, pending: 1, approved: 2 })[r.dd_status],
              render: (r) => <DiligenceCell r={r} caveats={result.caveats} /> },
            { key: "registry", label: "Own accounts · give.do", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.programme_expense_ratio_pct,
              render: (r) => <RegistryCell r={r} /> },
            { key: "consistency_label", label: "Consistency", align: "right",
              className: "whitespace-nowrap",
              sortValue: (r) => r.yoy_consistency_cv,
              render: (r) => r.consistency_label
                ? <Chip label={r.consistency_label} tone={r.consistency_label}
                        title={`Coefficient of variation ${r.yoy_consistency_cv} over ${r.consistency_fys_used?.join(", ")}`} />
                : <Chip label="under 3 FY" tone="muted" /> },
          ]}
        />
        <p className="text-[12px] text-stone-600 leading-relaxed max-w-3xl">
          * mGrant-internal comparison only, against beneficiaries declared as <em>reached</em>.
          Never compared to any external or peer figure. Hover any figure to see how it is
          calculated. Click a column to sort, or a row for the partner's grant ledger, tranche
          release and due-diligence detail.
          {result.dd_status_counts && (
            <>
              {" "}Due diligence standing is mGrant's own:{" "}
              {result.dd_status_counts.approved} approved, {result.dd_status_counts.pending} pending
              and {result.dd_status_counts.none} with no DD record — an absent record is a gap in
              the record, not proof diligence was never done.
            </>
          )}
          {result.registry_matched_partners != null && (
            <>
              {" "}The give.do column is a secondary cross-check of the partner's own published
              accounts — {result.registry_matched_partners} of{" "}
              {result.partner_total ?? result.rows.length} partners matched, unaudited by us.
            </>
          )}
        </p>
      </div>
    );
  }

  if (result.kind === "reallocation") {
    const cands = result.candidates || [];
    const cells = (result.cells || []).slice(0, 40);
    return (
      <div className="space-y-8">
        {result.aspirational_districts && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
               data-testid="adp-summary">
            <StatTile
              label="Aspirational districts funded"
              tone="brand"
              value={`${num(result.aspirational_districts.aspirational_districts_funded)} of ${result.aspirational_districts.aspirational_districts_in_list}`}
              title={result.aspirational_districts.source}
            />
            <StatTile
              label="Committed in those districts"
              tone="brand"
              value={inr(result.aspirational_districts.committed_in_aspirational_districts)}
              sub={`${pct(result.aspirational_districts.committed_share_in_aspirational_pct)} of the portfolio`}
              title={result.aspirational_districts.source}
            />
          </div>
        )}
        <div>
          <Label>Flagged for review — {cands.length} of {result.cell_count} {result.dimension}s</Label>
          {result.column_glossary && (
            <details className="mb-3 rounded-lg border border-stone-200 bg-white"
                     data-testid="reallocation-glossary">
              <summary className="cursor-pointer px-4 py-2.5 text-[13px] font-medium text-[#A01236] select-none">
                What these columns mean
              </summary>
              <dl className="px-4 pb-3.5 pt-1 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {Object.entries(result.column_glossary).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[13px] font-semibold text-stone-800">{k}</dt>
                    <dd className="text-[13px] text-stone-600 leading-snug">{v}</dd>
                  </div>
                ))}
                {result.materiality_rule && (
                  <div className="sm:col-span-2">
                    <dt className="text-[13px] font-semibold text-stone-800">
                      When a cell gets flagged
                    </dt>
                    <dd className="text-[13px] text-stone-600 leading-snug">
                      {result.threshold_rule}. {result.materiality_rule}
                    </dd>
                  </div>
                )}
              </dl>
            </details>
          )}
          {result.in_flight_note && (
            <p className="text-[13px] text-stone-600 mb-3" data-testid="in-flight-note">
              {num(result.in_flight_grant_count ?? 0)} of {num(result.grant_count)} grants are
              still in flight and excluded from these triggers.
            </p>
          )}
          <DataTable
            testId="reallocation-candidates"
            onRowClick={result.dimension === "district" && onDistrictClick
              ? (r) => r.cell_district && onDistrictClick(r)
              : undefined}
            rows={cands.slice(0, 40)}
            defaultSort={{ key: "spend_share_pct", order: "desc" }}
            columns={[
              { key: "cell", label: result.dimension.charAt(0).toUpperCase() + result.dimension.slice(1),
                sortValue: (r) => r.cell,
                className: "min-w-[20rem]", render: (r) => {
                const reasons = [...(r.cost_reasons || r.reasons || []),
                                 ...(r.data_quality_reasons || [])];
                const extra = reasons.length - 1;
                return (
                <div className="max-w-lg">
                  <div className="font-medium text-stone-900 flex items-center gap-2 flex-wrap">
                    {r.cell}
                    <AdpChip r={r} />
                  </div>
                  {reasons[0] && (
                    <div className="text-[13px] text-stone-600 mt-1 leading-snug"
                         title={[...reasons, ...(r.in_flight_context || [])].join("\n")}>
                      {reasons[0]}
                      {extra > 0 && (
                        <span className="text-stone-500"> · +{extra} more</span>
                      )}
                    </div>
                  )}
                </div>
              )}},
              { key: "spend_share_pct", label: "Share", align: "right",
                className: "whitespace-nowrap", sortValue: (r) => r.spend_share_pct,
                render: (r) => (
                  <span title={`${r.spend_share_pct}% of committed portfolio. Multi-district grants are split evenly across their districts.`}>
                    {pct(r.spend_share_pct)}
                  </span>
                )},
              { key: "utilisation_rate_pct", label: "Utilisation", align: "right",
                headerTitle: "Measured on grants whose period has ended. Money still in flight is excluded.",
                className: "whitespace-nowrap", sortValue: (r) => r.utilisation_rate_pct,
                render: (r) => (
                  <span title={utilTooltip(r) + "\nEnded grants only — money still in flight is excluded."}>
                    <Meter value={r.utilisation_rate_pct} />
                  </span>
                )},
              { key: "stalled_capital", label: "Stalled", align: "right",
                headerTitle: "Received minus utilised, on grants whose period has ended.",
                className: "whitespace-nowrap", sortValue: (r) => r.stalled_capital,
                render: (r) => r.stalled_capital > 0 ? (
                  <span title={`${inr(r.stalled_capital)} = ${inr(r.received)} received − ${inr(r.utilised)} utilised, on grants whose period has ended.`}>
                    {inr(r.stalled_capital)}
                  </span>
                ) : (
                  <span className="text-stone-500"
                        title={r.stalled_capital < 0
                          ? `Utilisation reported exceeds recorded receipts by ${inr(Math.abs(r.stalled_capital))} — a receipt-entry gap in mGrant, not spare money.`
                          : "Everything received on ended grants has been utilised."}>
                    {r.stalled_capital < 0 ? "none · receipts short" : "—"}
                  </span>
                )},
              { key: "in_flight_committed", label: "In flight", align: "right",
                headerTitle: "Committed to grants still inside their period or in an open financial year. Excluded from every trigger.",
                className: "whitespace-nowrap", sortValue: (r) => r.in_flight_committed,
                render: (r) => r.in_flight_committed
                  ? <span title="Committed to grants still inside their period or in an open financial year. Excluded from every trigger.">
                      {inr(r.in_flight_committed)}
                    </span>
                  : <span className="text-stone-500">—</span> },
              { key: "flag", label: "Flag", align: "right", sortValue: (r) => r.reasons.length,
                headerTitle: Object.entries(result.flag_legend || {})
                  .map(([k, v]) => `${k}: ${v}`).join("\n"),
                render: (r) => <Chip label={r.flag} tone={r.flag} /> },
            ]}
          />
        </div>
        <div>
          <Label>All {result.dimension}s by committed spend</Label>
          <DataTable
            testId="reallocation-cells"
            maxHeight="max-h-[26rem]"
            onRowClick={result.dimension === "district" && onDistrictClick
              ? (r) => r.cell_district && onDistrictClick(r)
              : undefined}
            rows={cells}
            defaultSort={{ key: "committed", order: "desc" }}
            columns={[
              { key: "cell", label: result.dimension.charAt(0).toUpperCase() + result.dimension.slice(1),
                sortValue: (r) => r.cell, render: (r) => (
                <span className="font-medium text-stone-900 inline-flex items-center gap-2 flex-wrap">
                  {r.cell}
                  <AdpChip r={r} />
                </span>
              )},
              { key: "grants", label: "Grants", align: "right", sortValue: (r) => r.grants },
              { key: "committed", label: "Committed", align: "right",
                className: "whitespace-nowrap", sortValue: (r) => r.committed,
                render: (r) => inr(r.committed) },
              { key: "spend_share_pct", label: "Share", align: "right",
                className: "whitespace-nowrap", sortValue: (r) => r.spend_share_pct,
                render: (r) => pct(r.spend_share_pct) },
              { key: "closed_utilisation_rate_pct", label: "Utilisation", align: "right",
                headerTitle: "Funds utilised as a share of funds received, counting only grants "
                  + "whose period has ended. Money still in flight is excluded.",
                className: "whitespace-nowrap",
                sortValue: (r) => r.closed_utilisation_rate_pct,
                render: (r) => r.closed_grants
                  ? <span title={`${inr(r.closed_stalled_capital)} of ${inr(r.closed_received)} received on ${r.closed_grants} ended grants is still unutilised.`}>
                      <Meter value={r.closed_utilisation_rate_pct} />
                    </span>
                  : <span className="text-stone-500" title="Every grant here is still in flight, so utilisation is not judged yet.">in flight</span> },
              { key: "in_flight_committed", label: "In flight", align: "right",
                headerTitle: "Committed to grants still inside their period or in an open "
                  + "financial year. Excluded from the utilisation column.",
                className: "whitespace-nowrap", sortValue: (r) => r.in_flight_committed,
                render: (r) => r.in_flight_committed
                  ? <span title={`${r.in_flight_grants} grants still in flight here.`}>
                      {inr(r.in_flight_committed)}
                    </span>
                  : <span className="text-stone-500">—</span> },
              { key: "cost_per_beneficiary_internal", label: "₹ / beneficiary", align: "right",
                headerTitle: "Utilised money divided by declared beneficiaries, across every grant "
                  + "in the cell. mGrant-internal only — there is no external benchmark.",
                className: "whitespace-nowrap", sortValue: (r) => r.cost_per_beneficiary_internal,
                render: (r) => r.cost_per_beneficiary_internal
                  ? inr(r.cost_per_beneficiary_internal)
                  : <Chip label="withheld" tone="muted" title={r.cost_per_beneficiary_withheld || "no beneficiary count declared"} /> },
            ]}
          />
        </div>
      </div>
    );
  }

  if (result.kind === "peer_benchmarking") {
    const b = result.benchmark || {};
    if (!b.available) {
      return (
        <div className="border border-rose-200 bg-rose-50/60 rounded-lg p-4" data-testid="peer-gated">
          <div className="text-xs font-semibold text-rose-700 mb-1">
            Peer figures withheld
          </div>
          <p className="text-sm text-stone-800 leading-relaxed">{b.reason}</p>
          <p className="text-[12px] text-stone-600 mt-2">
            Bajaj Finserv side of this cell: {inr(result.bajaj_committed_inr)} committed across{" "}
            {num(result.bajaj_grants)} grants. Shown because it is our own data, not peer data.
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Companies filing (k)" value={num(b.k)} />
          <Stat label="Peer total spend" value={inr(b.peer_total_spent_inr)} />
          <Stat label="Median company" value={inr(b.peer_median_company_spent_inr)} />
          <Stat label="Bajaj Finserv share of cell" value={pct(b.bajaj_share_of_cell_pct, 2)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Crowding (top-5 share)" value={pct(b.crowding_top5_share_pct)} />
          <Stat label="PSU share" value={pct(b.psu_share_pct)} />
        </div>
        <div>
          <Label>Largest funders in {b.state} · {b.theme} · {b.fy}</Label>
          <DataTable
            testId="peer-top-companies"
            maxHeight="max-h-72"
            rows={b.top_companies || []}
            columns={[
              { key: "company_name", label: "Company" },
              { key: "spent_inr", label: "Spend filed", align: "right", render: (r) => inr(r.spent_inr) },
            ]}
          />
        </div>
        <div>
          <Label>Peer portfolio shape (MCA sub-sector)</Label>
          <DataTable
            testId="peer-sub-sectors"
            rows={b.sub_sector_split || []}
            columns={[
              { key: "sub_sector", label: "Sub-sector" },
              { key: "spent_inr", label: "Spend", align: "right", render: (r) => inr(r.spent_inr) },
            ]}
          />
        </div>
      </div>
    );
  }

  if (result.kind === "peer_benchmarking_grid") {
    return (
      <DataTable
        testId="peer-grid"
        onRowClick={onRowClick}
        rows={result.rows || []}
        defaultSort={{ key: "peer_total_spent_inr", order: "desc" }}
        columns={[
          { key: "state_name", label: "State", sortValue: (r) => r.state_name, render: (r) => (
            <span className="font-medium text-stone-900">{r.state_name}</span>
          )},
          { key: "k", label: "Companies (k)", align: "right", sortValue: (r) => r.k,
            render: (r) => (
              <span className={r.gated ? "text-rose-700" : ""}>{num(r.k)}</span>
            )},
          { key: "peer_total_spent_inr", label: "All peers", align: "right",
            headerTitle: "Every company that filed CSR spend in this state, theme and year (MCA).",
            className: "whitespace-nowrap", sortValue: (r) => r.peer_total_spent_inr,
            render: (r) => r.gated
              ? <Chip label="withheld" tone="erratic" title={r.reason} />
              : inr(r.peer_total_spent_inr) },
          { key: "bfsi_spent_inr", label: "BFSI peers", align: "right",
            headerTitle: "BFSI slice of the same cell. Membership is inferred from the filed "
              + "company name (MCA carries no industry code), so it is a range: the core counts "
              + "only unambiguous names, the upper bound adds names common in BFSI but also used "
              + "outside it. Bajaj Finserv's own filed entities sit inside this figure.",
            className: "whitespace-nowrap", sortValue: (r) => r.bfsi_spent_inr,
            render: (r) => r.bfsi_gated
              ? <Chip label="withheld" tone="erratic" title={r.bfsi_reason} />
              : (
                <div className="leading-tight"
                     title={`Core: ${r.bfsi_k} companies. Upper bound: ${r.bfsi_k_upper} companies.`}>
                  <div className="tabular-nums">{inr(r.bfsi_spent_inr)}</div>
                  <div className="text-[12px] text-stone-600 tabular-nums">
                    to {inr(r.bfsi_spent_inr_upper)}
                    {r.bfsi_share_of_peers_pct != null && ` · ${r.bfsi_share_of_peers_pct}% of peers`}
                  </div>
                </div>
              ) },
          { key: "crowding_top5_share_pct", label: "Crowding (top 5)", align: "right",
            className: "whitespace-nowrap", sortValue: (r) => r.crowding_top5_share_pct,
            render: (r) => r.gated
              ? <span className="text-stone-500">—</span>
              : <Meter value={r.crowding_top5_share_pct} tone="warn" /> },
          { key: "bajaj_committed_inr", label: "Bajaj Finserv committed", align: "right",
            className: "whitespace-nowrap", sortValue: (r) => r.bajaj_committed_inr,
            render: (r) => r.bajaj_present
              ? inr(r.bajaj_committed_inr)
              : <Chip label="absent" tone="review" /> },
        ]}
      />
    );
  }

  if (result.kind === "need_vs_funding_state") {
    const rows = result.states_ranked || [];
    const themeLabel = result.theme_label || result.theme || "this theme";
    return (
      <div className="space-y-4">
        {result.focus_state_portfolio && (
          <FocusState portfolio={result.focus_state_portfolio}
                      row={result.focus_state_row}
                      note={result.focus_note}
                      indicatorLabel={result.indicator_meta?.label}
                      themeLabel={themeLabel} />
        )}
        <div className="text-[13px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
             data-testid="nfi-theme-scope">
          Every Bajaj Finserv figure below covers <strong>{themeLabel}</strong> only — the portfolio
          share is a share of committed {themeLabel} spend, not of all Bajaj Finserv CSR. Click a
          state to trace its grants.
        </div>
        <NeedGapChart rows={rows.slice(0, 12)} themeLabel={themeLabel} onRowClick={onRowClick} />
        <DataTable
          testId="nfi-state-table"
          maxHeight="max-h-[34rem]"
          onRowClick={onRowClick}
          rows={rows}
          defaultSort={{ key: "need_vs_funding_index", order: "desc" }}
          columns={[
            { key: "state_name", label: "State", sortValue: (r) => r.state_name, render: (r) => (
              <span className="font-medium text-stone-900">{r.state_name}</span>
            )},
            { key: "indicator_value", label: result.indicator_meta?.label || "Indicator", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.indicator_value,
              render: (r) => num(r.indicator_value) },
            { key: "need_score", label: "Need", align: "right", sortValue: (r) => r.need_score,
              render: (r) => <Meter value={r.need_score} tone="warn" /> },
            { key: "bajaj_committed_inr", label: `Committed · ${themeLabel}`, align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.bajaj_committed_inr,
              render: (r) => !r.bajaj_committed_inr
                ? <Chip label="no presence" tone="muted" />
                : (
                  <div title={grantTooltip(r)}>
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      <span>{inr(r.bajaj_committed_inr)}</span>
                      {r.bajaj_apportioned_only
                        ? <Chip label="apportioned" tone="review" title={grantTooltip(r)} />
                        : null}
                    </span>
                    <SplitNote r={r} />
                  </div>
                )},
            { key: "bajaj_share_of_theme_portfolio_pct", label: `Share of ${themeLabel} portfolio`,
              align: "right", className: "whitespace-nowrap",
              sortValue: (r) => r.bajaj_share_of_theme_portfolio_pct,
              render: (r) => pct(r.bajaj_share_of_theme_portfolio_pct, 2) },
            { key: "peer_csr_spend_inr", label: "Peer CSR spend", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.peer_csr_spend_inr,
              render: (r) => inr(r.peer_csr_spend_inr) },
            { key: "need_vs_funding_index", label: "Index", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.need_vs_funding_index,
              render: (r) => (
                <span className={`font-semibold ${r.need_vs_funding_index > 40 ? "text-rose-700"
                  : r.need_vs_funding_index > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {r.need_vs_funding_index > 0 ? "+" : ""}{score(r.need_vs_funding_index)}
                </span>
              )},
          ]}
        />
        <p className="text-[12px] text-stone-600 leading-relaxed">{rows[0]?.formula}</p>
      </div>
    );
  }

  if (result.kind === "peer_benchmarking_mca_basis") {
    const rows = result.rows || [];
    const filed = rows.filter((r) => r.bajaj_mca_filed_inr);
    return (
      <div className="space-y-4">
        <div className="text-[13px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
             data-testid="mca-basis-note">
          Both sides are MCA filings, so all {rows.length} filed years compare like-for-like.
          Bajaj Finserv group here means{" "}
          <strong>{(result.bajaj_group_entities || []).join(", ")}</strong>. The mGrant column is
          committed value, a different measure — shown only for{" "}
          {(result.mgrant_overlap_fys || []).join(" and ")}, never added to the filed figure.
        </div>
        {result.bfsi_tagging && (
          <div className="text-[13px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
               data-testid="bfsi-note">
            <strong>BFSI peers</strong> are tagged from the filed company name, because the MCA
            file carries no industry code — so the set is a range, not a number. The core counts
            only unambiguous names (BANK, INSURANCE, SECURITIES, HOUSING FINANCE and the like); the
            upper bound adds names such as CAPITAL, HOLDINGS or FINANCIAL that are common in BFSI
            but also appear outside it. Hover any BFSI figure for the range and the sub-sector
            split. Bajaj Finserv's own entities sit inside this set, so a BFSI share is a share of
            a group it belongs to, not of an outside market.
          </div>
        )}
        <MiniBar
          data={filed.map((r) => ({ name: r.fy.replace("FY-", "FY"), v: r.bajaj_mca_filed_inr }))}
          xKey="name" yKey="v" label="₹ filed with MCA"
        />
        <DataTable
          testId="mca-basis-table"
          rows={rows}
          columns={[
            { key: "fy", label: "Financial year", render: (r) => (
              <span className="font-medium text-stone-900">{r.fy}</span>
            )},
            { key: "bajaj_mca_filed_inr", label: "Bajaj Finserv filed (MCA)", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.bajaj_mca_filed_inr,
              render: (r) => r.bajaj_mca_filed_inr
                ? <span title={`${r.bajaj_filed_rows} filed project rows · ${(r.bajaj_entities_filing || []).join(", ")}`}>
                    {inr(r.bajaj_mca_filed_inr)}
                  </span>
                : <Chip label="no filing" tone="muted" /> },
            { key: "peer_company_count", label: "Companies (k)", align: "right",
              sortValue: (r) => r.peer_company_count, render: (r) => num(r.peer_company_count) },
            { key: "peer_total_spent_inr", label: "Peer spend (MCA)", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.peer_total_spent_inr,
              render: (r) => r.peer_total_spent_inr === null
                ? <Chip label="withheld" tone="erratic" title={r.peer_withheld_reason} />
                : inr(r.peer_total_spent_inr) },
            { key: "bajaj_share_of_cell_pct", label: "Share of all peers", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.bajaj_share_of_cell_pct,
              render: (r) => r.bajaj_share_of_cell_pct === null
                ? <span className="text-stone-500">—</span>
                : pct(r.bajaj_share_of_cell_pct, 2) },
            { key: "bfsi_spent_inr", label: "BFSI peer spend", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.bfsi_spent_inr,
              render: (r) => r.bfsi_spent_inr === null
                ? <Chip label="withheld" tone="erratic" title={r.bfsi_withheld_reason} />
                : (
                  <div title={bfsiTooltip(r)}>
                    <span>{inr(r.bfsi_spent_inr)}</span>
                    <div className="text-[12px] text-stone-600 mt-0.5 font-sans">
                      up to {inr(r.bfsi_spent_inr_upper)} · {num(r.bfsi_company_count)} companies
                    </div>
                  </div>
                )},
            { key: "bajaj_share_of_bfsi_pct", label: "Share of BFSI peers", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.bajaj_share_of_bfsi_pct,
              render: (r) => r.bajaj_share_of_bfsi_pct === null
                ? <span className="text-stone-500">—</span>
                : (
                  <div title={bfsiTooltip(r)}>
                    <span className="font-semibold">{pct(r.bajaj_share_of_bfsi_pct, 1)}</span>
                    <div className="text-[12px] text-stone-600 mt-0.5 font-sans">
                      {pct(r.bajaj_share_of_bfsi_pct_lower, 1)} at the wider tagging
                    </div>
                  </div>
                )},
            { key: "mgrant_committed_inr", label: "mGrant committed", align: "right",
              className: "whitespace-nowrap", sortValue: (r) => r.mgrant_committed_inr,
              render: (r) => r.mgrant_committed_inr === null
                ? <Chip label="pre-mGrant" tone="muted" title="mGrant holds no grants for this year" />
                : <span title="Committed value in mGrant. A different measure from filed spend — never added to it.">
                    {inr(r.mgrant_committed_inr)}
                  </span> },
          ]}
        />
      </div>
    );
  }

  if (result.kind === "need_vs_funding") {
    const rows = result.districts_ranked || [];
    return (
      <div className="space-y-4">
        <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-[12px] text-amber-900">
          District need inputs are seeded placeholders. Use the state view for published indicators.
        </div>
        <DataTable
          testId="nfi-district-table"
          maxHeight="max-h-[34rem]"
          rows={rows}
          defaultSort={{ key: "need_vs_funding_index", order: "desc" }}
          columns={[
            { key: "district_name", label: "District", render: (r) => (
              <div>
                <div className="font-medium text-stone-900">{r.district_name}</div>
                <div className="text-[12px] text-stone-600">
                  {r.state_name} · {r.lgd_code}
                  {r.is_aspirational_district ? " · aspirational" : ""}
                </div>
              </div>
            )},
            { key: "indicator_value", label: "Indicator", align: "right", render: (r) => num(r.indicator_value) },
            { key: "need_score", label: "Need", align: "right", render: (r) => score(r.need_score) },
            { key: "bajaj_inr_per_1000_pop", label: "₹ / 1k pop", align: "right",
              render: (r) => inr(r.bajaj_inr_per_1000_pop) },
            { key: "need_vs_funding_index", label: "Index", align: "right",
              render: (r) => score(r.need_vs_funding_index) },
          ]}
        />
      </div>
    );
  }

  if (result.kind === "data_reality") {
    const rep = result.report || {};
    return (
      <div className="space-y-3">
        <DataTable
          testId="reality-blockers"
          rows={rep.blockers || []}
          columns={[
            { key: "capability", label: "Capability" },
            { key: "status", label: "Status" },
            { key: "evidence", label: "Evidence" },
          ]}
        />
      </div>
    );
  }

  return (
    <p className="text-xs text-stone-600 py-4" data-testid="numbers-empty">
      Nothing was computed for this question.
    </p>
  );
};

const Stat = ({ label, value, title }) => (
  <div className="border border-stone-200 rounded-lg px-3 py-2 bg-stone-50/60" title={title}>
    <div className="text-[12px] text-stone-600">{label}</div>
    <div className="num text-sm font-medium text-stone-900 mt-0.5">{value}</div>
  </div>
);

const Label = ({ children }) => (
  <div className="text-[14px] font-semibold text-stone-800 mb-2">
    {children}
  </div>
);

/** One named state, answered directly: what we fund there today and where it stands on need. */
const FocusState = ({ portfolio, row, note, indicatorLabel, themeLabel }) => (
  <section className="rounded-xl border border-[#F3C6D0] bg-[#FDF2F4] p-4 space-y-3"
           data-testid="focus-state">
    <div className="flex items-baseline justify-between gap-3 flex-wrap">
      <h3 className="text-[15px] font-semibold text-stone-900">
        {portfolio.state_name} — what we fund today
      </h3>
      <span className="text-[12px] text-stone-600">
        all themes · {portfolio.fy_range?.[0]}–{portfolio.fy_range?.[portfolio.fy_range.length - 1]}
      </span>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
      <StatTile label="Committed" tone="brand" value={inr(portfolio.committed_inr)}
                sub="state share of every grant" />
      <StatTile label="Grants" tone="sky" value={num(portfolio.grants)}
                sub={`${num(portfolio.partners)} partners`} />
      <StatTile label="Districts reached" tone="sky" value={num(portfolio.districts_we_fund)}
                sub={portfolio.aspirational_districts_we_fund?.length
                  ? `${portfolio.aspirational_districts_we_fund.length} aspirational`
                  : "none on the aspirational list"} />
      {row && (
        <StatTile label={indicatorLabel || "Need vs funding"}
                  tone={row.need_vs_funding_index > 0 ? "amber" : "emerald"}
                  value={`${row.need_vs_funding_index > 0 ? "+" : ""}${score(row.need_vs_funding_index)}`}
                  sub={`need ${score(row.need_score)} vs ${themeLabel} funding ${score(row.funding_score)}`} />
      )}
    </div>
    <div className="flex flex-wrap gap-1.5" data-testid="focus-theme-split">
      {Object.entries(portfolio.committed_by_theme_inr || {}).map(([t, v]) => (
        <span key={t}
              className="inline-flex items-center gap-1.5 text-[12px] text-stone-700 bg-white border border-stone-200 rounded-full px-2.5 py-0.5">
          {t}
          <span className="num font-semibold text-stone-900">{inr(v)}</span>
        </span>
      ))}
    </div>
    {portfolio.aspirational_districts_we_fund?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 items-center" data-testid="focus-adp">
        <span className="text-[12px] text-stone-600">Aspirational districts funded here:</span>
        {portfolio.aspirational_districts_we_fund.map((d) => (
          <Chip key={d} label={d} tone="review" />
        ))}
      </div>
    )}
    {note && <p className="text-[13px] text-stone-700 leading-relaxed">{note}</p>}
    <p className="text-[12px] text-stone-600 leading-relaxed">{portfolio.basis}</p>
  </section>
);
